import { getTRPCClient } from "@repo/api";
import { mintEntityId } from "@repo/domain";
import { canonicalBase, canonicalUrl } from "@repo/fhir-systems";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import {
  buildProgramDefinitionRowsQuery,
  createProgramDefinitionActions,
  type PlanCollection,
  type ProgramRow,
} from "./_program-definition-internal";
import { projectIncludedProgramDefinition } from "./program-definition-projection";

const COLLECTION = "planDefinition";
/** Every program canonical this app mints sits directly under this base. */
export const PROGRAM_DEFINITION_CANONICAL_BASE = canonicalBase("PlanDefinition/program");

/** Program-owned vocabulary. Public DTOs do not depend on mutation input types. */
export type ProgramDefinitionTimeWindow =
  | "early_morning"
  | "mid_morning"
  | "midday"
  | "early_afternoon"
  | "late_afternoon";

export interface ProgramDefinitionDto {
  definitionUrl: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: "Active" | "Inactive";
  days: Array<{
    dayId: string;
    dayNumber: number;
    sessions: Array<{
      sessionId: string;
      timeWindow: ProgramDefinitionTimeWindow;
      protocolDefinitionUrl: string;
      protocolName: string;
      totalDurationMinutes?: number;
    }>;
  }>;
}

export interface ProgramDefinitionFilters {
  availability?: ProgramDefinitionDto["availability"];
  search?: string;
}

const plans = () => getDataStoreClient().getCollection<ProgramRow, string>(COLLECTION) as PlanCollection;

export function mintProgramDefinitionUrl(): string {
  return canonicalUrl("PlanDefinition/program", mintEntityId());
}

export function useProgramDefinition(definitionUrl: string | undefined): ProgramDefinitionDto | undefined {
  const collection = useCollection(COLLECTION) as PlanCollection;
  const result = useLiveQuery({
    queryKey: [collection.id, "program-definition", definitionUrl ?? null],
    query: (query) => {
      if (!definitionUrl) return undefined;
      return buildProgramDefinitionRowsQuery(query, collection)
        .where(({ definition }) => eq(definition.url, definitionUrl))
        .findOne();
    },
  });
  const row = result.data as (ProgramRow & { relatedPlanDefinitions: unknown }) | undefined;
  // TanStack DB requires correlated `toArray()` includes in structured `.select()` and forbids child queries in
  // `.fn.select()`. `toArray()` re-emits this parent when Protocol rows change, so this pure projection keeps one
  // reactive subscription without nesting the structured query and losing its included rows in TanStack DB 0.8.6.
  return row ? projectIncludedProgramDefinition(row) : undefined;
}

export function useProgramDefinitions(filters: ProgramDefinitionFilters = {}): ProgramDefinitionDto[] {
  const collection = useCollection(COLLECTION) as PlanCollection;
  const result = useLiveQuery({
    queryKey: [collection.id, "program-definitions", filters.availability ?? null, filters.search ?? null],
    query: (query) => buildProgramDefinitionRowsQuery(query, collection, filters),
  });
  // TanStack DB requires correlated `toArray()` includes in structured `.select()` and forbids child queries in
  // `.fn.select()`. `toArray()` re-emits each parent when Protocol rows change, so this pure projection keeps one
  // reactive subscription without nesting the structured query and losing its included rows in TanStack DB 0.8.6.
  return ((result.data as Array<ProgramRow & { relatedPlanDefinitions: unknown }> | undefined) ?? []).map(
    projectIncludedProgramDefinition,
  );
}

const actions = createProgramDefinitionActions({
  collection: plans,
  persist: {
    define: (input, options) => getTRPCClient().catalog.programDefinition.define.mutate(input, options),
    revise: (input, options) => getTRPCClient().catalog.programDefinition.revise.mutate(input, options),
    correct: (input, options) => getTRPCClient().catalog.programDefinition.correct.mutate(input, options),
    activate: (input, options) => getTRPCClient().catalog.programDefinition.activate.mutate(input, options),
    deactivate: (input, options) => getTRPCClient().catalog.programDefinition.deactivate.mutate(input, options),
  },
});

export const defineProgramDefinition = actions.defineProgramDefinition;
export const reviseProgramDefinition = actions.reviseProgramDefinition;
export const correctProgramDefinition = actions.correctProgramDefinition;
export const activateProgramDefinition = actions.activateProgramDefinition;
export const deactivateProgramDefinition = actions.deactivateProgramDefinition;
